import { Chat, type Chat as ChatInstance } from '@pubnub/chat';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  PUBNUB_HEARTBEAT_INTERVAL,
  PUBNUB_PRESENCE_TIMEOUT,
  PUBNUB_PUBLISH_KEY,
  PUBNUB_SUBSCRIBE_KEY,
} from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useChatStore } from '@avanta/shared/stores/chat/index';

export interface ChatUser {
  id: string;
  firstName?: string | null;
  surname?: string | null;
  name?: string | null;
  nickname?: string | null;
}

export interface UseChatOptions {
  currentUser: ChatUser | null | undefined;
  userId?: string;
}

export const useChat = ({
  currentUser,
  userId,
}: UseChatOptions): { chat: ChatInstance | null; isLoading: boolean } => {
  const [chat, setChat] = useState<ChatInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  const currentUserId = currentUser?.id;
  const userName =
    currentUser?.firstName && currentUser.surname
      ? `${currentUser.firstName} ${currentUser.surname}`
      : currentUser?.name || currentUser?.nickname;

  useEffect(() => {
    let cancelled = false;

    if (!currentUserId) {
      setChat(null);
      setIsLoading(false);
      return (): void => {
        cancelled = true;
      };
    }

    setChat(null);
    setIsLoading(true);

    const isOwnChat = !userId || userId === currentUserId;

    const initChat = async (): Promise<void> => {
      try {
        const chatInstance = await Chat.init({
          publishKey: PUBNUB_PUBLISH_KEY,
          subscribeKey: PUBNUB_SUBSCRIBE_KEY,
          userId: userId || currentUserId,
          typingTimeout: 5000,
          presenceTimeout: PUBNUB_PRESENCE_TIMEOUT,
          heartbeatInterval: PUBNUB_HEARTBEAT_INTERVAL,
        });

        if (cancelled) return;

        if (isOwnChat) {
          await chatInstance.updateUser(currentUserId, { name: userName ?? undefined });
          if (cancelled) return;

          useChatStore.setState({
            chat: chatInstance,
            currentUser: chatInstance.currentUser,
            isInitialized: true,
            error: null,
          });
        }

        setChat(chatInstance);
      } catch (error) {
        if (cancelled) return;

        toast.error(t('ChatStore.error', { ns: LOCIZE_NS.CHAT }), {
          description: error instanceof Error ? error.message : t('ChatStore.errorDescription'),
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void initChat();

    return (): void => {
      cancelled = true;
    };
  }, [currentUserId, userId, userName, t]);

  return { chat, isLoading };
};
